import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import {
  AppError,
  ErrorFactory,
  ForbiddenError,
  NotFoundError,
  ServerError,
  UnauthorizedError,
  ValidationError,
} from '../../shared/errors/app-error';

/**
 * Service for handling and navigating to error pages
 * Provides a clean API for components to trigger error navigation
 */
@Injectable({ providedIn: 'root' })
export class ErrorService {
  private readonly router = inject(Router);

  /**
   * Navigate to a generic error page with error information
   */
  navigateToError(error: unknown, options?: NavigationExtras): Promise<boolean> {
    const appError = ErrorFactory.fromHttpError(error);
    return this.navigateWithError(appError, options);
  }

  /**
   * Navigate to 404 Not Found page
   */
  navigateToNotFound(message?: string, options?: NavigationExtras): Promise<boolean> {
    const error = new NotFoundError(message);
    return this.navigateWithError(error, {
      ...options,
      queryParams: { ...options?.queryParams, status: 404 },
    });
  }

  /**
   * Navigate to 500 Server Error page
   */
  navigateToServerError(message?: string, options?: NavigationExtras): Promise<boolean> {
    const error = new ServerError(message);
    return this.navigateWithError(error, {
      ...options,
      queryParams: { ...options?.queryParams, status: 500 },
    });
  }

  /**
   * Navigate to 401 Unauthorized page (triggers login redirect)
   */
  navigateToUnauthorized(message?: string, options?: NavigationExtras): Promise<boolean> {
    const error = new UnauthorizedError(message);
    return this.navigateWithError(error, options);
  }

  /**
   * Navigate to 403 Forbidden page
   */
  navigateToForbidden(message?: string, options?: NavigationExtras): Promise<boolean> {
    const error = new ForbiddenError(message);
    return this.navigateWithError(error, {
      ...options,
      queryParams: { ...options?.queryParams, status: 403 },
    });
  }

  /**
   * Navigate to validation error page
   */
  navigateToValidationError(
    message: string,
    validationErrors: Record<string, string[]>,
    options?: NavigationExtras,
  ): Promise<boolean> {
    const error = new ValidationError(message, validationErrors);
    return this.navigateWithError(error, {
      ...options,
      queryParams: { ...options?.queryParams, status: 422 },
    });
  }

  /**
   * Navigate to error page with custom status code
   */
  navigateToStatusCode(
    statusCode: number,
    message?: string,
    options?: NavigationExtras,
  ): Promise<boolean> {
    const error = ErrorFactory.fromHttpError({ status: statusCode, message: message || 'Error' });
    return this.navigateWithError(error, {
      ...options,
      queryParams: { ...options?.queryParams, status: statusCode },
    });
  }

  /**
   * Core navigation method that handles error serialization
   */
  private navigateWithError(error: AppError, options?: NavigationExtras): Promise<boolean> {
    // Serialize error info for query params
    const errorInfo = error.toErrorInfo();
    const queryParams = {
      ...options?.queryParams,
      error: JSON.stringify(errorInfo),
      // Also include individual params for backward compatibility
      status: errorInfo.statusCode,
      code: errorInfo.code,
      message: errorInfo.message,
      traceId: errorInfo.traceId,
    };

    // Determine target route based on error type
    let targetRoute = '/error';
    if (errorInfo.statusCode === 404) {
      targetRoute = '/not-found';
    } else if (errorInfo.statusCode >= 500) {
      targetRoute = '/server-error';
    }

    return this.router.navigate([targetRoute], {
      ...options,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Handle an error and optionally navigate
   * Useful for catch blocks in components
   */
  async handleError(
    error: unknown,
    options?: { navigate?: boolean; navigationExtras?: NavigationExtras; fallbackMessage?: string },
  ): Promise<AppError> {
    const appError = ErrorFactory.fromHttpError(error);

    // Log error
    console.error('[ErrorService] Handled error:', appError.toErrorInfo());

    // Navigate if requested
    if (options?.navigate) {
      await this.navigateWithError(appError, options.navigationExtras);
    }

    return appError;
  }

  /**
   * Clear any stored error state
   */
  clearErrorState(): void {
    // Could clear any stored error in state management
  }

  /**
   * Get a user-friendly error message from any error
   */
  getErrorMessage(error: unknown): string {
    const appError = ErrorFactory.fromHttpError(error);
    return appError.userMessage;
  }

  /**
   * Check if error is a specific type
   */
  isErrorType(
    error: unknown,
    type: 'network' | 'server' | 'client' | 'auth' | 'validation',
  ): boolean {
    const appError = ErrorFactory.fromHttpError(error);

    switch (type) {
      case 'network':
        return appError.statusCode === 0;
      case 'server':
        return appError.statusCode >= 500;
      case 'client':
        return appError.statusCode >= 400 && appError.statusCode < 500;
      case 'auth':
        return appError.statusCode === 401 || appError.statusCode === 403;
      case 'validation':
        return appError.statusCode === 422;
      default:
        return false;
    }
  }

  /**
   * Retry a failed operation
   */
  async retry<T>(operation: () => Promise<T>, maxRetries = 3, delayMs = 1000): Promise<T> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        // Don't retry on client errors (4xx)
        const appError = ErrorFactory.fromHttpError(error);
        if (appError.statusCode >= 400 && appError.statusCode < 500) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
        }
      }
    }

    throw lastError;
  }
}

/**
 * Helper function to create error service instance for use outside Angular DI
 * (e.g., in interceptors or guards)
 */
export function createErrorService(router: Router): ErrorService {
  // This is a workaround - in practice, inject ErrorService where needed
  // This function is mainly for use in functional interceptors
  return new (class extends ErrorService {
    constructor() {
      super();
      // @ts-expect-error - manually set router
      this.router = router;
    }
  })();
}
